/**
 * PlannedLayerTray Component
 *
 * 行動版底部托盤，顯示計劃任務並支持點擊快速排程。
 *
 * 功能：
 * - 點擊展開/收合
 * - 收合時顯示任務數量和狀態指示器
 * - 展開時按優先級分組顯示任務
 * - 支持點擊任務快速排程（非拖曳）
 */
import React, { useCallback } from 'react';
import { Task } from '../../../types/task';
import { usePlannedTasks, PlannedTaskGroups } from '../../../hooks/usePlannedTasks';
import PlannedTaskGroup from './PlannedTaskGroup';
import {
  ChevronUp,
  ChevronDown,
  Calendar,
  CalendarClock,
  AlertCircle,
  CalendarDays,
  ClipboardList,
  Inbox,
} from 'lucide-react';

// 托盤高度常數
const TRAY_HEIGHT_COLLAPSED = 56; // 收合時的高度
const TRAY_HEIGHT_EXPANDED = '30vh'; // 展開時的高度

export interface PlannedLayerTrayProps {
  /** 是否展開 */
  isExpanded: boolean;
  /** 切換展開狀態 */
  onToggle: () => void;
  /** 點擊快速排程回調 */
  onQuickSchedule?: (task: Task) => void;
  /** 進入放置模式回調 */
  onEnterPlacementMode?: (task: Task) => void;
  /** 點擊查看任務詳情 */
  onViewTaskDetail?: (task: Task) => void;
  /** 高亮指定 Task ID */
  highlightTaskId?: string | null;
  /** 清除高亮回調 */
  onClearHighlight?: () => void;
}

/**
 * 獲取分組配置
 */
function getGroupConfigs(groups: PlannedTaskGroups) {
  return [
    {
      key: 'today',
      title: 'Today',
      tasks: groups.today,
      icon: <Calendar className="w-4 h-4" />,
      countClassName: 'bg-sky-100 text-sky-700',
      defaultExpanded: true,
    },
    {
      key: 'thisWeek',
      title: 'This Week',
      tasks: groups.thisWeek,
      icon: <CalendarDays className="w-4 h-4" />,
      countClassName: 'bg-green-100 text-green-700',
      defaultExpanded: true,
    },
    {
      key: 'overdue',
      title: 'Overdue',
      tasks: groups.overdue,
      icon: <AlertCircle className="w-4 h-4" />,
      countClassName: 'bg-red-100 text-red-700',
      defaultExpanded: true,
    },
    {
      key: 'thisMonth',
      title: 'This Month',
      tasks: groups.thisMonth,
      icon: <CalendarClock className="w-4 h-4" />,
      countClassName: 'bg-sky-100 text-sky-700',
      defaultExpanded: false,
    },
    {
      key: 'later',
      title: 'Later',
      tasks: groups.later,
      icon: <ClipboardList className="w-4 h-4" />,
      countClassName: 'bg-gray-100 text-gray-600',
      defaultExpanded: false,
    },
    {
      key: 'other',
      title: 'Other',
      tasks: groups.other,
      icon: <Inbox className="w-4 h-4" />,
      countClassName: 'bg-gray-100 text-gray-500',
      defaultExpanded: false,
    },
  ];
}

const PlannedLayerTray: React.FC<PlannedLayerTrayProps> = ({
  isExpanded,
  onToggle,
  onQuickSchedule,
  onEnterPlacementMode,
  onViewTaskDetail,
  highlightTaskId,
  onClearHighlight,
}) => {
  const { groups, totalCount, todayCount, overdueCount, isLoading, isEmpty } = usePlannedTasks();

  // 處理快速排程
  const handleQuickSchedule = useCallback(
    (task: Task) => {
      onQuickSchedule?.(task);
    },
    [onQuickSchedule]
  );

  // 處理查看詳情
  const handleViewDetail = useCallback(
    (task: Task) => {
      onViewTaskDetail?.(task);
    },
    [onViewTaskDetail]
  );

  // 分組配置
  const groupConfigs = getGroupConfigs(groups);

  return (
    <div
      className={`
        fixed bottom-16 left-0 right-0
        bg-white border-t border-gray-200 shadow-sm
        transition-all duration-300 ease-in-out
        z-floating
      `}
      style={{
        height: isExpanded ? TRAY_HEIGHT_EXPANDED : TRAY_HEIGHT_COLLAPSED,
      }}
    >
      {/* 托盤標題/手柄 */}
      <button
        onClick={onToggle}
        className={`
          w-full flex items-center justify-between
          pl-4 pr-20 py-3
          bg-white border-b border-gray-100
          active:bg-gray-50
          transition-colors
        `}
        style={{ height: TRAY_HEIGHT_COLLAPSED }}
      >
        <div className="flex items-center gap-3">
          {/* 圖標和標題 */}
          <ClipboardList className="w-5 h-5 text-sky-600" />
          <span className="font-medium text-gray-800">Tasks</span>

          {/* 任務數量 */}
          {totalCount > 0 && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
              {totalCount}
            </span>
          )}

          {/* 狀態指示器（收合時顯示） */}
          {!isExpanded && totalCount > 0 && (
            <div className="flex items-center gap-1.5 ml-2">
              {todayCount > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-sky-500" />
                  <span className="text-xs text-gray-500">{todayCount}</span>
                </div>
              )}
              {overdueCount > 0 && (
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-xs text-gray-500">{overdueCount}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 展開/收合箭頭 */}
        <div className="p-1 text-gray-400">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronUp className="w-5 h-5" />
          )}
        </div>
      </button>

      {/* 內容區（展開時顯示） */}
      {isExpanded && (
        <div
          className="flex-1 overflow-y-auto px-4 py-3"
          style={{
            height: `calc(${TRAY_HEIGHT_EXPANDED} - ${TRAY_HEIGHT_COLLAPSED}px)`,
          }}
        >
          {/* Loading 狀態 */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-sky-500 border-t-transparent" />
            </div>
          )}

          {/* 空狀態 */}
          {!isLoading && isEmpty && (
            <div className="text-center py-8">
              <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No tasks</p>
              <p className="text-xs text-gray-400 mt-1">
                Set a plan date on tasks to see them here
              </p>
            </div>
          )}

          {/* 任務分組 */}
          {!isLoading && !isEmpty && (
            <div>
              {/* 操作提示 */}
              <div className="text-xs text-gray-500 mb-4">
                Tap a task to schedule it on the calendar
              </div>

              {groupConfigs.map((config) => (
                <PlannedTaskGroup
                  key={config.key}
                  title={config.title}
                  tasks={config.tasks}
                  icon={config.icon}
                  countClassName={config.countClassName}
                  defaultExpanded={config.defaultExpanded}
                  draggable={false}
                  onQuickSchedule={handleQuickSchedule}
                  onEnterPlacementMode={onEnterPlacementMode}
                  onViewDetail={handleViewDetail}
                  compact={true}
                  highlightTaskId={highlightTaskId}
                  onClearHighlight={onClearHighlight}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlannedLayerTray;
